/**
 * Format duration in milliseconds to human-readable format
 * @param {number} duration - Duration in milliseconds
 * @returns {string} Formatted duration string (e.g., "05m 30s")
 */
export const formatDuration = (duration) => {
  const totalSeconds = Math.floor(duration / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
};


/**
 * Extract a specific parameter value from URL query string
 * @param {string} url - URL string with query parameters
 * @param {string} key - Parameter key to extract
 * @returns {string|null} Parameter value or null if not found
 */
export const getUrlValue = (url, key) => {
  const queryString = url.startsWith('?') ? url.substring(1) : url;
  const params = new URLSearchParams(queryString);
  return params.get(key);
};

/**
 * Format date to Turkish locale date string
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date string (DD.MM.YYYY)
 */
export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * Format date to Turkish locale date and time string
 * @param {Date|string} date - Date object or date string
 * @returns {string|null} Formatted date and time string (DD.MM.YYYY HH:MM) or null if invalid
 */
export const formatDateTime = (date) => {
  if (!date) {
    return null;
  }

  return new Date(date).toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Parse query string into object with typed values
 * Automatically converts numeric values to numbers
 * @param {string} queryString - Query string (with or without leading ?)
 * @returns {Object} Object with parsed key-value pairs
 */
export const parseQueryString = (queryString) => {
  if (!queryString) {
    return {};
  }
  if (queryString.startsWith("?")) {
    queryString = queryString.substring(1);
  }

  const params = queryString.split("&");
  const result = {};

  params.forEach(param => {
    const [key, value] = param.split("=");

    if (!isNaN(value) && value.trim() !== "") {
      result[key] = Number(value);
    } else {
      result[key] = decodeURIComponent(value);
    }
  });

  return result;
}

/**
 * Check if two dates are on the same day
 * @param {Date} d1 - First date
 * @param {Date} d2 - Second date
 * @returns {boolean} True if dates are on the same day
 */
export const isSameDay = (d1, d2) => {
  return d1?.toDateString() === d2?.toDateString();
};